import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { HostedAgentFormState } from "../utils/hosted-agents"
import type { HostedAgent } from "@/api/admin/hosted-agents"
import type { SkillPackageInstallPreview, SkillPackageSummary } from "@/api/admin/skill-packages"

import {
  installSkillPackage,
  uninstallSkillPackage,
  upgradeSkillPackage,
} from "@/api/admin/skill-packages"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"

import { HostedAgentSkillInstallDiffCard } from "./hosted-agent-skill-install-diff-card"
import { useSkillPackageCatalogQuery } from "../hooks/queries/skill-packages"
import { HOSTED_AGENT_SKILL_IDS } from "../utils/hosted-agents"
import { describeSkillPackageError, type SkillPackageOperation } from "../utils/skill-packages"

type SkillsTabProps = {
  form: HostedAgentFormState
  patch: (partial: Partial<HostedAgentFormState>) => void
  /** The saved agent (undefined while creating): installed packages are
   *  SERVER-MANAGED pins mutated through their own endpoints, never the
   *  buffered form PUT. */
  agent?: HostedAgent
  /** Refetch the agent after an install/uninstall/upgrade landed. */
  onInstalledChanged?: () => void
}

type InstallFlowState =
  | { step: "idle" }
  | { step: "previewing"; pkg: SkillPackageSummary }
  | { step: "confirm"; pkg: SkillPackageSummary; preview: SkillPackageInstallPreview }
  | { step: "applying"; pkg: SkillPackageSummary }

export function HostedAgentSkillsTab({ form, patch, agent, onInstalledChanged }: SkillsTabProps) {
  const { t } = useTranslation()
  const catalogQuery = useSkillPackageCatalogQuery()
  const [flow, setFlow] = useState<InstallFlowState>({ step: "idle" })
  const [busyPackageId, setBusyPackageId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  /** B1/G1: say WHY, never axios's "Request failed with status code 409". */
  const showError = (exc: unknown, operation: SkillPackageOperation) => {
    const described = describeSkillPackageError(exc, operation)
    const text = t(described.key, { defaultValue: described.defaultValue })
    setError(described.detail && described.detail !== text ? `${text} (${described.detail})` : text)
  }

  // Built-in runtime skills: unchanged semantics (form.skills, buffered PUT).
  // The hardcoded list stays as the offline fallback for a failed catalog read.
  const builtinCatalog = useMemo(() => {
    const fromServer = (catalogQuery.data?.builtin_skills ?? []).map((entry) => entry.name)
    return fromServer.length > 0 ? fromServer : [...HOSTED_AGENT_SKILL_IDS]
  }, [catalogQuery.data])
  const extras = form.skills.filter((skill) => !builtinCatalog.includes(skill))
  const allBuiltins = [...builtinCatalog, ...extras]

  const installed = agent?.skill_packages ?? []
  const installedIds = new Set(installed.map((entry) => entry.package_id))
  const allPackages = [
    ...(catalogQuery.data?.packages ?? []),
    ...(catalogQuery.data?.platform_packages ?? []),
  ]
  const installable = allPackages.filter(
    (pkg) => pkg.latest_version > 0 && !installedIds.has(pkg.id)
  )
  const latestByPackageId = useMemo(() => {
    const map = new Map<string, number>()
    for (const pkg of [
      ...(catalogQuery.data?.packages ?? []),
      ...(catalogQuery.data?.platform_packages ?? []),
    ]) {
      map.set(pkg.id, pkg.latest_version)
    }
    return map
  }, [catalogQuery.data])

  const toggleBuiltin = (skill: string, checked: boolean) => {
    patch({
      skills: checked ? [...form.skills, skill] : form.skills.filter((entry) => entry !== skill),
    })
  }

  const runPreview = async (pkg: SkillPackageSummary) => {
    if (!agent) return
    setError(null)
    setFlow({ step: "previewing", pkg })
    try {
      const preview = await installSkillPackage(agent.id, { package_id: pkg.id, dry_run: true })
      setFlow({ step: "confirm", pkg, preview })
    } catch (exc) {
      showError(exc, "install")
      setFlow({ step: "idle" })
    }
  }

  const confirmInstall = async () => {
    if (!agent || flow.step !== "confirm") return
    const pkg = flow.pkg
    setError(null)
    setFlow({ step: "applying", pkg })
    try {
      await installSkillPackage(agent.id, { package_id: pkg.id })
      setFlow({ step: "idle" })
      onInstalledChanged?.()
    } catch (exc) {
      showError(exc, "install")
      setFlow({ step: "idle" })
    }
  }

  const runUninstall = async (packageId: string) => {
    if (!agent) return
    setError(null)
    setBusyPackageId(packageId)
    try {
      await uninstallSkillPackage(agent.id, packageId)
      onInstalledChanged?.()
    } catch (exc) {
      showError(exc, "uninstall")
    } finally {
      setBusyPackageId(null)
    }
  }

  const runUpgrade = async (packageId: string) => {
    if (!agent) return
    setError(null)
    setBusyPackageId(packageId)
    try {
      await upgradeSkillPackage(agent.id, packageId, {})
      onInstalledChanged?.()
    } catch (exc) {
      showError(exc, "install")
    } finally {
      setBusyPackageId(null)
    }
  }

  const needsLoadSkill =
    (form.skills.length > 0 || installed.length > 0) && !form.tools.includes("load_skill")

  return (
    <div className="space-y-5">
      <p className="text-muted-foreground text-sm">
        {t("adminDashboard.hostedAgents.skillsTab.help", {
          defaultValue:
            "Skill packs give the agent packaged capabilities (document creation, comms formats) at runtime.",
        })}
      </p>
      {needsLoadSkill ? (
        <p className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-sm">
          {t("adminDashboard.hostedAgents.skillsTab.needsLoadSkillTool", {
            defaultValue:
              "Skill packs only reach the agent through the load_skill tool — enable it in the Tools tab, or these stay unused.",
          })}
        </p>
      ) : null}
      {error ? (
        <p className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-sm">
          {error}
        </p>
      ) : null}

      {/* Installed skill packages: server-managed pins, own endpoints. */}
      <section className="space-y-2">
        <h4 className="text-sm font-medium">
          {t("adminDashboard.hostedAgents.skillsTab.installedTitle", {
            defaultValue: "Installed skill packages",
          })}
        </h4>
        {!agent ? (
          <p className="text-muted-foreground text-sm">
            {t("adminDashboard.hostedAgents.skillsTab.saveFirst", {
              defaultValue: "Save the agent first — packages are installed on a saved agent.",
            })}
          </p>
        ) : null}
        {agent && installed.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {t("adminDashboard.hostedAgents.skillsTab.noneInstalled", {
              defaultValue: "No skill packages installed yet.",
            })}
          </p>
        ) : null}
        {agent && installed.length > 0 ? (
          <div className="space-y-2">
            {installed.map((entry) => {
              const latest = latestByPackageId.get(entry.package_id)
              const upgradable = typeof latest === "number" && latest > entry.version
              const busy = busyPackageId === entry.package_id
              return (
                <div
                  key={entry.package_id}
                  className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-md border p-3"
                >
                  <span className="flex min-w-0 flex-1 basis-48 flex-col">
                    <span className="font-mono text-sm wrap-anywhere">
                      {entry.name}
                      <span className="text-muted-foreground"> @ v{entry.version}</span>
                    </span>
                    {upgradable ? (
                      <span className="text-muted-foreground text-xs">
                        {t("adminDashboard.hostedAgents.skillsTab.newerVersion", {
                          defaultValue: "Version {{version}} is published.",
                          version: latest,
                        })}
                      </span>
                    ) : null}
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    {upgradable ? (
                      <Button
                        disabled={busy}
                        size="sm"
                        variant="outline"
                        onClick={() => runUpgrade(entry.package_id)}
                      >
                        {t("adminDashboard.hostedAgents.skillsTab.upgrade", {
                          defaultValue: "Upgrade",
                        })}
                      </Button>
                    ) : null}
                    <Button
                      disabled={busy}
                      size="sm"
                      variant="ghost"
                      onClick={() => runUninstall(entry.package_id)}
                    >
                      {t("adminDashboard.hostedAgents.skillsTab.uninstall", {
                        defaultValue: "Uninstall",
                      })}
                    </Button>
                    {busy ? <Spinner className="size-4" /> : null}
                  </span>
                </div>
              )
            })}
          </div>
        ) : null}

        {agent && installable.length > 0 && flow.step === "idle" ? (
          <div className="space-y-2 pt-1">
            <h5 className="text-muted-foreground text-xs font-medium uppercase">
              {t("adminDashboard.hostedAgents.skillsTab.availableTitle", {
                defaultValue: "Available to install",
              })}
            </h5>
            {installable.map((pkg) => (
              // The row must never be wider than the dialog: `truncate` (nowrap)
              // text made its min-content the full description, which pushed
              // the dialog's grid wider than the viewport and hid Install and
              // the footer. Wrap anywhere and clamp instead; the button drops
              // below the text on a narrow dialog.
              <div
                key={pkg.id}
                className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-md border border-dashed p-3"
                data-testid="skill-package-available-row"
              >
                <span className="flex min-w-0 flex-1 basis-48 flex-col gap-0.5">
                  <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-mono text-sm wrap-anywhere">
                      {pkg.name}
                      <span className="text-muted-foreground"> @ v{pkg.latest_version}</span>
                    </span>
                    {pkg.source === "platform" ? (
                      <Badge variant="secondary">
                        {t("adminDashboard.hostedAgents.skillsTab.platformBadge", {
                          defaultValue: "Platform",
                        })}
                      </Badge>
                    ) : null}
                  </span>
                  {pkg.description ? (
                    <span
                      className="text-muted-foreground line-clamp-2 text-xs wrap-anywhere"
                      title={pkg.description}
                    >
                      {pkg.description}
                    </span>
                  ) : null}
                </span>
                <Button className="shrink-0" size="sm" onClick={() => runPreview(pkg)}>
                  {t("adminDashboard.hostedAgents.skillsTab.install", { defaultValue: "Install" })}
                </Button>
              </div>
            ))}
          </div>
        ) : null}

        {flow.step === "previewing" || flow.step === "applying" ? (
          <div className="text-muted-foreground flex items-center gap-2 text-sm">
            <Spinner className="size-4" />
            {flow.step === "previewing"
              ? t("adminDashboard.hostedAgents.skillsTab.previewing", {
                  defaultValue: "Computing what installing {{name}} would change…",
                  name: flow.pkg.name,
                })
              : t("adminDashboard.hostedAgents.skillsTab.applying", {
                  defaultValue: "Installing {{name}}…",
                  name: flow.pkg.name,
                })}
          </div>
        ) : null}

        {flow.step === "confirm" ? (
          <HostedAgentSkillInstallDiffCard
            preview={flow.preview}
            onCancel={() => setFlow({ step: "idle" })}
            onConfirm={confirmInstall}
          />
        ) : null}
      </section>

      {/* Built-in runtime skills: unchanged buffered-form semantics. */}
      <section className="space-y-2">
        <h4 className="text-sm font-medium">
          {t("adminDashboard.hostedAgents.skillsTab.builtinTitle", {
            defaultValue: "Built-in runtime skills",
          })}
        </h4>
        <div className="space-y-2">
          {allBuiltins.map((skill) => {
            const checked = form.skills.includes(skill)
            return (
              <label
                key={skill}
                className="hover:bg-accent/50 flex cursor-pointer items-center justify-between gap-3 rounded-md border p-3 transition-colors"
                htmlFor={`hosted-agent-skill-${skill}`}
              >
                <span className="flex flex-col">
                  <span className="font-mono text-sm">{skill}</span>
                  {!builtinCatalog.includes(skill) ? (
                    <span className="text-muted-foreground text-xs">
                      {t("adminDashboard.hostedAgents.skillsTab.unknownSkill", {
                        defaultValue: "Not in the known skill registry",
                      })}
                    </span>
                  ) : null}
                </span>
                <Switch
                  checked={checked}
                  id={`hosted-agent-skill-${skill}`}
                  onCheckedChange={(value) => toggleBuiltin(skill, value)}
                />
              </label>
            )
          })}
        </div>
      </section>
    </div>
  )
}
